import { action, computed, makeObservable, observable, runInAction } from 'mobx';
import type { IAccessRequestWaiting } from '@featit/shared';
import { decisionApiFor, type IDecision } from '@/services/DecisionApi';
import { describeLoadFailure, type ILoadFailure } from '@/services/loadFailure';
import type { IWorkItemEvent } from '@/services/WorkItemApi';
import { DECISION_ITEM_TYPE_KEY } from '@featit/shared/question-codes';
import {
  createAttentionPoller,
  PUSH_SETTLE_MS,
  type AttentionPoller,
  type AttentionSubscriber,
} from './attentionPoller';

/**
 * What the store needs from the live channel (I-0635, AC-2): join one work area's shared stream and
 * hear its work-item events and its reconnects. `subscribeToWorkAreaRealtime` satisfies it; the
 * store is handed it rather than importing it, so a test that builds a store for something else
 * never opens a socket (see `stores/index.ts`).
 */
export type DecisionsRealtimeSubscriber = (
  workAreaId: string,
  listener: { onEvent?: (event: IWorkItemEvent) => void; onResume?: () => void },
) => () => void;

export interface DecisionsStoreOptions {
  /** The live channel. Omitted, the store keeps current by its poll and focus triggers alone. */
  subscribeToWorkArea?: DecisionsRealtimeSubscriber;
  /**
   * F-0236: the signed-in person's own attention stream. It rings for a decision filed for, answered
   * by or taken from the reader in any work area, so the badge follows under All work areas too.
   */
  subscribeToAttention?: AttentionSubscriber;
}

/** A work-item event that can change what is waiting for somebody: one about a decision. */
export function isDecisionEvent(event: IWorkItemEvent): boolean {
  return event.workItem?.itemType?.key === DECISION_ITEM_TYPE_KEY;
}

/**
 * What the current set is about: one work area, or every work area this reader may read (I-0560).
 *
 * ⛔ A scope is a value, not a flag pair, because "which work area" and "all of them" are answers to
 * the same question and a set must never be shown under the other one. `null` is neither: nothing
 * has been asked for yet.
 */
export const ALL_WORK_AREAS_SCOPE = 'all-work-areas';
/**
 * The prefix a namespace scope is held under (F-0383, §10.9) — the same
 * `viewScopeKey`/`NAMESPACE_SCOPE_PREFIX` convention `@featit/shared` and `ProjectStore` use, so a
 * namespace id is told apart from a work area id by this prefix rather than a third field to keep
 * in sync with `scope`.
 */
export const NAMESPACE_SCOPE_PREFIX = 'namespace:';
export type DecisionsScopeType = string | typeof ALL_WORK_AREAS_SCOPE;

/** A scope's rows and when they were read (RD-23). */
interface ICarriedSet {
  waiting: IDecision[];
  answeredAt: string | null;
  /** AC-SV18: the access requests read beside them, or `null` when that read had not answered. */
  accessRequests: IAccessRequestWaiting[] | null;
}

/**
 * "Decisions waiting for me" — one query, read by two surfaces (I-0480, DS-44).
 *
 * The sidebar's *Decisions for me* badge and the decisions page must show the same number, and the
 * only way two surfaces cannot disagree is to read the same store. Before this the page fetched
 * `decisionsWaitingForMe` for itself and the nav entry carried no count at all; with 38 decisions
 * imported for Jay the entry read the same as it would with none.
 *
 * ⛔ **Not the load-once pattern**, where a count is fetched only while it is `undefined` and
 * nothing ever invalidates it. On a to-do list that is worse than no badge: a person sees 38,
 * answers ten, and still sees 38. This store is re-read (DS-45):
 *
 * - when the work area changes (`load`);
 * - when the page's selected decision stops being `open` — the DS-11 signal, which is the event
 *   that changes the set from inside the app (`refresh`);
 * - **at once when the reader comes back to the tab** — `focus`, `visibilitychange` to visible, and
 *   `pageshow` from Safari's back-forward cache (I-0635, AC-3);
 * - **every 30 s while the window is focused, and every 60 s while the tab is hidden or the window
 *   is unfocused** (I-0635, AC-1). Before I-0635 the poll stopped on `blur`, so a background tab's
 *   title count froze until the tab was activated — the tab it was added for (I-0575, I-0611);
 * - **about a second after a decision in the current work area is filed, answered, refused or
 *   reassigned**, heard on the work area's shared `workItemEvents` stream (I-0635, AC-2). That stream
 *   is a `graphql-ws` socket, so it rings in a background tab too, and the poll becomes its backstop.
 *   A reconnect re-reads once for whatever the gap swallowed.
 *
 * The cadences, the single timer and the rest live in `createAttentionPoller`, shared with
 * `NotificationStore` so the two counts cannot drift apart again.
 *
 * **Under All work areas the person's own attention stream rings instead** (F-0236): one
 * subscription, across every work area, rather than a fleet of per-work-area listeners.
 *
 * ⚠️ **Limit.** Safari suspends a tab that stays hidden for long; no script runs there, so nothing
 * can refresh its title until it is shown, and then it refreshes at once.
 */
export class DecisionsStore {
  /**
   * What the current set is about: a work-area id, {@link ALL_WORK_AREAS_SCOPE}, or `null` before
   * the first request.
   */
  scope: DecisionsScopeType | null = null;
  /** `null` until the first read for this scope has answered. */
  waiting: IDecision[] | null = null;
  failure: ILoadFailure | null = null;
  /**
   * I-1077: when `waiting` was last read successfully (ISO). A refresh that fails leaves the old set
   * on screen, and the failure banner says how old it is from this — never a stale list that looks
   * current. `null` while nothing has been read for the scope.
   */
  answeredAt: string | null = null;
  loading = false;
  /**
   * F-0713 (AC-SV18): the access requests waiting for this reader's answer in the same scope, listed
   * beside the decisions as approvals and counted in the badge with them. `null` until read. Read
   * apart from the decisions, so a failure of one never blanks the other.
   */
  accessRequests: IAccessRequestWaiting[] | null = null;
  /** The last read of {@link accessRequests} failed; what is on screen may be older, or nothing. */
  accessRequestsFailed = false;

  private poller: AttentionPoller | null = null;
  private readonly subscribeToWorkArea: DecisionsRealtimeSubscriber | null;
  private unsubscribeRealtime: (() => void) | null = null;
  private unsubscribeAttention: (() => void) | null = null;
  private requestSerial = 0;

  constructor(options: DecisionsStoreOptions = {}) {
    this.subscribeToWorkArea = options.subscribeToWorkArea ?? null;
    makeObservable(this, {
      scope: observable,
      waiting: observable.ref,
      failure: observable.ref,
      answeredAt: observable,
      loading: observable,
      accessRequests: observable.ref,
      accessRequestsFailed: observable,
      workAreaId: computed,
      allWorkAreas: computed,
      namespaceId: computed,
      waitingCount: computed,
      unknown: computed,
      load: action,
      loadAllWorkAreas: action,
      loadNamespace: action,
      refresh: action,
      reset: action,
      enterWorkArea: action,
    });
    if (typeof window !== 'undefined') {
      this.poller = createAttentionPoller({ read: () => this.refresh() });
      // A read waits the settle time: filing a decision announces it before its question exists.
      this.unsubscribeAttention =
        options.subscribeToAttention?.({
          onChange: (change) => {
            if (change.reason !== 'read') this.poller?.soon(PUSH_SETTLE_MS);
          },
          onResume: () => this.poller?.soon(0),
        }) ?? null;
    }
  }

  /**
   * The work area the current set belongs to, or `null` under the All work areas or a namespace
   * scope.
   */
  get workAreaId(): string | null {
    return this.scope === null || this.scope === ALL_WORK_AREAS_SCOPE || this.namespaceId !== null
      ? null
      : this.scope;
  }

  /** Whether the current set spans every work area this reader may read. */
  get allWorkAreas(): boolean {
    return this.scope === ALL_WORK_AREAS_SCOPE;
  }

  /** The namespace the current set is scoped to (F-0383, §10.9), or `null` outside that scope. */
  get namespaceId(): string | null {
    return this.scope?.startsWith(NAMESPACE_SCOPE_PREFIX)
      ? this.scope.slice(NAMESPACE_SCOPE_PREFIX.length)
      : null;
  }

  /**
   * The badge's number: `undefined` while the first read is in flight, so the sidebar shows `...`.
   *
   * ⭐ I-0560 (AC-5): this is the length of whatever the current scope asked for, so the badge counts
   * every work area under All and the selected one alone under a single scope — and it cannot
   * disagree with the list beneath it, because the list is the same array.
   */
  get waitingCount(): number | undefined {
    if (this.waiting === null) return undefined;
    // AC-SV18: a request waiting for the reader's answer is waiting for them as a decision is.
    return this.waiting.length + (this.accessRequests?.length ?? 0);
  }

  /** The first read failed and nothing is known: the badge says nothing rather than `...` forever. */
  get unknown(): boolean {
    return this.waiting === null && this.failure !== null;
  }

  /** Read the set for a work area. A different scope drops the old set before asking. */
  async load(workAreaId: string): Promise<void> {
    await this.loadScope(workAreaId);
  }

  /**
   * Read the set across every work area this reader may read (I-0560).
   *
   * ⛔ **One request.** Not a loop over the selector's work areas: the server has the cross-area
   * query, and a loop here would be N round trips for the page and N again for the badge.
   */
  async loadAllWorkAreas(): Promise<void> {
    await this.loadScope(ALL_WORK_AREAS_SCOPE);
  }

  /** Read the set across one namespace's whole subtree (F-0383, §10.9) — one request, as above. */
  async loadNamespace(namespaceId: string): Promise<void> {
    await this.loadScope(`${NAMESPACE_SCOPE_PREFIX}${namespaceId}`);
  }

  /**
   * `load`/`loadAllWorkAreas` are **idempotent per scope** (I-0587, AC-2). Several components ask
   * defensively for the scope they need — the shell (`Layout.tsx`), the decisions pages, and the
   * Home blocks each have their own effect that calls this on mount and on scope change — rather
   * than threading a single loader instance through props that do not otherwise need it. That is
   * fine, and is meant to stay fine: `waiting`/`load`/`refresh` is a small, well-understood
   * contract, and coordinating five call sites through one prop chain would be more coupling than
   * the problem is worth.
   *
   * ⛔ What is NOT fine is a caller asking for the scope the store is **already on** re-issuing the
   * request. That used to happen every time, so the several-callers-is-fine design above produced
   * a real duplicate request per caller, once each, every time any of their dependencies changed —
   * five identical answers to one question on the decisions page under React's dev-mode double
   * invocation. Asking again for the current scope is now a no-op: nothing is fetched, nothing is
   * cleared. A **genuine** scope change still always fetches, and `refresh()` still always
   * re-reads in place regardless of scope — that is the explicit "read it again" path (DS-45's
   * focus/poll/resolved-decision triggers all call `refresh` directly, never `load`).
   */
  private async loadScope(scope: DecisionsScopeType): Promise<void> {
    if (this.scope === scope) return;
    const carried = this.carriedInto(scope);
    this.scope = scope;
    this.waiting = carried?.waiting ?? null;
    this.accessRequests = carried?.accessRequests ?? null;
    this.accessRequestsFailed = false;
    this.failure = null;
    // Carried rows keep the age of the read they came from, so a failed re-read says how old.
    this.answeredAt = carried?.answeredAt ?? null;
    this.followScope(scope);
    await this.refresh();
  }

  /** RD-23: the last set each scope answered this session, with when, for a return to the scope. */
  private readonly lastSets = new Map<DecisionsScopeType, ICarriedSet>();

  /**
   * RD-23 (I-1077, `docs/concept-mobile-app.md`): the rows a scope can show while its read is under
   * way — never "nothing" when they are known. Measured at 6× CPU: on resume the app's tab root
   * follows the scope from All to the person's one work area, and the callers of this one-scope store
   * moved it All → work area → All → work area, each move dropping the set. With the read stalled
   * the screen said "couldn't be loaded" under a badge of 2.
   *
   * The scope's own last answer, if it had one; else, narrowing from All to a work area, that work
   * area's rows of the All set — the very rows its own read returns. Nothing otherwise: a narrower
   * set cannot stand for a wider one, nor one work area's for another's (I-0958, WS-3).
   */
  private carriedInto(scope: DecisionsScopeType): ICarriedSet | null {
    const own = this.lastSets.get(scope);
    if (own) return own;
    const all = this.lastSets.get(ALL_WORK_AREAS_SCOPE);
    if (!all || scope === ALL_WORK_AREAS_SCOPE || scope.startsWith(NAMESPACE_SCOPE_PREFIX)) {
      return null;
    }
    return {
      waiting: all.waiting.filter(
        (decision) => (decision.item.workAreaId ?? decision.question.workAreaId) === scope,
      ),
      answeredAt: all.answeredAt,
      accessRequests: all.accessRequests?.filter((request) => request.workAreaId === scope) ?? null,
    };
  }

  /** Re-read the current scope's set in place; the old rows stay up until the new ones arrive. */
  async refresh(): Promise<void> {
    const scope = this.scope;
    if (!scope) return;
    const serial = ++this.requestSerial;
    this.loading = true;
    // Across work areas, and a namespace, are the main connection's question until F-0379 merges
    // every connection's answer; one work area asks the deployment that holds it (I-1120).
    const decisionsRead = (async (): Promise<unknown> =>
      scope.startsWith(NAMESPACE_SCOPE_PREFIX)
        ? decisionApiFor(null).waitingForMeInNamespace(scope.slice(NAMESPACE_SCOPE_PREFIX.length))
        : scope === ALL_WORK_AREAS_SCOPE
          ? decisionApiFor(null).waitingForMeEverywhere()
          : decisionApiFor(scope).waitingForMe(scope))();
    // AC-SV18: the access requests are asked after the decisions, beside them, never before them.
    void this.refreshAccessRequests(scope, serial);
    try {
      const next: unknown = await decisionsRead;
      // An answer without the list is a failed read, not a slow one: left as `null` it would hold the
      // badge at `...` forever, claiming to load something that is never coming.
      if (!Array.isArray(next))
        throw new Error('The server answered without the decisions waiting for you.');
      runInAction(() => {
        // A slower, older answer must not overwrite a newer one, nor land on another scope.
        if (serial !== this.requestSerial || this.scope !== scope) return;
        this.waiting = next as IDecision[];
        this.failure = null;
        this.answeredAt = new Date().toISOString();
        this.lastSets.set(scope, {
          waiting: this.waiting,
          answeredAt: this.answeredAt,
          accessRequests: this.accessRequests,
        });
      });
    } catch (error: unknown) {
      runInAction(() => {
        if (serial !== this.requestSerial || this.scope !== scope) return;
        this.failure = describeLoadFailure(error);
      });
    } finally {
      runInAction(() => {
        if (serial === this.requestSerial) this.loading = false;
      });
    }
  }

  /**
   * AC-SV18: the access requests of the scope, read beside its decisions. A failure keeps what was
   * shown and says so ({@link accessRequestsFailed}); it never fails the decisions' read.
   */
  private async refreshAccessRequests(scope: DecisionsScopeType, serial: number): Promise<void> {
    try {
      const api = decisionApiFor(
        scope === ALL_WORK_AREAS_SCOPE || scope.startsWith(NAMESPACE_SCOPE_PREFIX) ? null : scope,
      );
      const next = await api.accessRequestsWaiting(
        scope.startsWith(NAMESPACE_SCOPE_PREFIX)
          ? { namespaceId: scope.slice(NAMESPACE_SCOPE_PREFIX.length) }
          : scope === ALL_WORK_AREAS_SCOPE
            ? { allWorkAreas: true }
            : { workAreaId: scope },
      );
      if (!Array.isArray(next)) throw new Error('The server answered without the access requests.');
      runInAction(() => {
        if (serial !== this.requestSerial || this.scope !== scope) return;
        this.accessRequests = next;
        this.accessRequestsFailed = false;
        const last = this.lastSets.get(scope);
        if (last) this.lastSets.set(scope, { ...last, accessRequests: next });
      });
    } catch {
      runInAction(() => {
        if (serial !== this.requestSerial || this.scope !== scope) return;
        this.accessRequestsFailed = true;
      });
    }
  }

  /**
   * I-0958 (WS-3): entering another work area drops this set before the page renders, so no row of
   * the previous one is drawn under the new one. The same work area keeps it.
   */
  enterWorkArea(workAreaId: string): void {
    if (this.scope === workAreaId) return;
    // RD-23: rows known to be this work area's stay up while it is read again in place.
    if (this.carriedInto(workAreaId)) {
      void this.loadScope(workAreaId);
      return;
    }
    this.dropSet();
  }

  /** Everything, the sets remembered per scope too. */
  reset(): void {
    this.dropSet();
    this.lastSets.clear();
  }

  private dropSet(): void {
    this.followScope(null);
    this.scope = null;
    this.waiting = null;
    this.accessRequests = null;
    this.accessRequestsFailed = false;
    this.failure = null;
    this.answeredAt = null;
    this.loading = false;
    this.requestSerial += 1;
  }

  dispose(): void {
    this.followScope(null);
    this.unsubscribeAttention?.();
    this.unsubscribeAttention = null;
    this.poller?.dispose();
    this.poller = null;
  }

  /**
   * Join the live channel for the scope's work area, leaving the previous one's (I-0635, AC-2).
   *
   * Only decision events ask for a read — a busy work area moves many items a minute, and none of
   * the others change what is waiting. The read waits {@link PUSH_SETTLE_MS} because filing a
   * decision announces the item before its question is written, and the waiting set is read off the
   * question.
   */
  private followScope(scope: DecisionsScopeType | null): void {
    this.unsubscribeRealtime?.();
    this.unsubscribeRealtime = null;
    // F-0383, §10.9: a namespace spans many work areas, same as All work areas — there is no one
    // work area's channel to join, so the poller (and the reader's own attention stream) carries it.
    if (
      !this.subscribeToWorkArea ||
      !scope ||
      scope === ALL_WORK_AREAS_SCOPE ||
      scope.startsWith(NAMESPACE_SCOPE_PREFIX)
    ) {
      return;
    }
    this.unsubscribeRealtime = this.subscribeToWorkArea(scope, {
      onEvent: (event) => {
        if (isDecisionEvent(event)) this.poller?.soon(PUSH_SETTLE_MS);
      },
      onResume: () => this.poller?.soon(0),
    });
  }
}
